import React from "react";
import UnclaimButton from "@components/FlowActionButtons/UnclaimButton";
import { useNotification } from "@context/NotificationContext";
import { canUserUnclaim } from "@features/vessel-call/vessel-call-status/ShippingAgencyTransfer/flow/utils";
import type { ShippingAgencyTransfer } from "@services/shipping-agency-transfer/types";
import { unclaimShippingAgencyTransferById } from "@services/shippingAgencyTransferApi";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { runTransaction } from "@telemetry/runTransaction";
import { useTranslation } from "react-i18next";

interface UnclaimShippingAgencyTransferProps {
  transferDetails: ShippingAgencyTransfer | null;
}

const LANG_BASE = "vessel-call.shipping-agency-transfer";
const SERVICE = "bff-vessel-call";

const UnclaimShippingAgencyTransfer: React.FC<UnclaimShippingAgencyTransferProps> = ({
  transferDetails,
}) => {
  const { notify } = useNotification();
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const unclaimMutation = useMutation({
    mutationFn: (id: string) =>
      runTransaction(
        {
          module: "vessel_call",
          screen: "ShippingAgencyTransfer",
          action: "unclaim_shipping_agency_transfer",
          entityType: "shipping_agency_transfer",
        },
        (transactionId) =>
          unclaimShippingAgencyTransferById({ service: SERVICE, id, transactionId }),
      ),
    onSuccess: async () => {
      const vesselCallId = transferDetails?.vesselCallId;

      if (vesselCallId) {
        await queryClient.invalidateQueries({
          queryKey: ["getShippingAgencyTransfers", vesselCallId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["getLatestShippingAgencyTransfer", vesselCallId],
        });
      }

      notify({
        title: t(`${LANG_BASE}.notifications.transfer-unclaimed`),
        type: "success",
      });
    },
    onError: (error: Error) =>
      notify({
        title: t("common.error"),
        description: error?.message ?? t("common.something-went-wrong"),
        type: "error",
      }),
  });

  if (!transferDetails?.task) {
    return null;
  }

  const isActionAvailable = canUserUnclaim(
    transferDetails.task.status,
    transferDetails.task.claimOwnerName,
  );

  const handleUnclaim = () => {
    unclaimMutation.mutate(transferDetails.id);
  };

  return (
    <UnclaimButton
      langBase={`${LANG_BASE}.flow-buttons`}
      permission="canUnclaimShippingAgencyTransfer"
      isActionAvailable={isActionAvailable}
      onUnclaim={handleUnclaim}
    />
  );
};

export default UnclaimShippingAgencyTransfer;
